import React, { useContext } from 'react';
import { Filter } from '../../common/components/types';
import { FORM_WIDGET_COMPONENTS, METADATA_NAMES } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormPanel from '../../common/form/FormPanel';
import FormWidget from '../../common/form/FormWidget';
import I18n from '../../common/i18n/I18n';
import { runtimesToSelectOptions } from '../../common/utils/arrays';
import { ProductionOptionRenderer } from '../../supply/booking/ProductionOptionRenderer';
import {
    Production_Field_Names,
    getOptionForProductionForm,
    shouldHideShowAttribute
} from '../helpers/productionUtils';
import { PRODUCTION_TYPES_OR_SUB_TYPES } from '../productionConstants';
import ProductionScheduleFormWidget from '../schedule/ProductionScheduleFormWidget';
import OwnerOrganizationFormItem from './OwnerOrganizationFormItem';
import ProductionTrackFormItems from './production/ProductionTrackFormItems';

export const runtimes = ['30', '40', '60', '90', '120'];

type ProductionFormItemsType = {
    enableConvertingToManaged?: boolean;
    onCreateHandle?: (e) => void;
    productionFilter?: Filter;
    hiddenFields?: Record<string, boolean>;
    focusField?: string;
};

export default function ProductionFormItems({
    enableConvertingToManaged = false,
    onCreateHandle = undefined,
    hiddenFields = {},
    productionFilter = undefined,
    focusField
}: ProductionFormItemsType) {
    const { state } = useContext(FormContext);
    return (
        <FormPanel>
            <FormContainer>
                <FormItem half hidden={hiddenFields[Production_Field_Names.name]}>
                    {enableConvertingToManaged ? (
                        <FormWidget
                            name="name"
                            component="ReferenceAutocomplete"
                            entity="Production"
                            label={<I18n token="production.form.field.name" />}
                            OptionRenderer={ProductionOptionRenderer}
                            getOption={getOptionForProductionForm}
                            preload={false}
                            allowFreeForm
                            filter={productionFilter}
                            notOnlyRef
                        />
                    ) : (
                        <FormWidget name="name.label" label={<I18n token="production.form.field.name" />} />
                    )}
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.otherTitles]}>
                    <FormWidget name="otherTitles" label={<I18n token="production.form.field.aka" />} mapTo={'AKA'} />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.productionType]}>
                    <FormWidget
                        name="productionType"
                        label={<I18n token="production.form.field.type" />}
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName="ShowType"
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.productionSubType]}>
                    <FormWidget
                        name="productionSubType"
                        label={<I18n token="production.form.field.subtype" />}
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName={METADATA_NAMES.productionSubtype}
                        allowCreate
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.genre]}>
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName="Genre"
                        name="genre"
                        label={<I18n token="production.form.field.genre" />}
                        multiple
                    />
                </FormItem>
                <FormItem
                    hidden={
                        (state?.productionType !== PRODUCTION_TYPES_OR_SUB_TYPES.LIMITED_SERIES ||
                            hiddenFields?.[Production_Field_Names.showAttributes]) &&
                        (state?.productionType === PRODUCTION_TYPES_OR_SUB_TYPES.SEASON ||
                            shouldHideShowAttribute(PRODUCTION_TYPES_OR_SUB_TYPES.SERIES, state, hiddenFields))
                    }
                >
                    <FormWidget
                        component={'Number'}
                        name="showAttributes"
                        label={<I18n token="production.form.field.noOfEpisodes" />}
                        mapTo="noOfEpisodes"
                    />
                </FormItem>
                <FormItem
                    half
                    hidden={shouldHideShowAttribute(PRODUCTION_TYPES_OR_SUB_TYPES.SEASON, state, hiddenFields)}
                >
                    <FormWidget
                        component={'Number'}
                        name="showAttributes"
                        label={<I18n token="production.form.field.seasonNo" />}
                        mapTo="seasonNo"
                    />
                </FormItem>
                <FormItem
                    half
                    hidden={
                        state?.productionType === PRODUCTION_TYPES_OR_SUB_TYPES.SERIES ||
                        state?.productionType === PRODUCTION_TYPES_OR_SUB_TYPES.LIMITED_SERIES ||
                        shouldHideShowAttribute(PRODUCTION_TYPES_OR_SUB_TYPES.SEASON, state, hiddenFields)
                    }
                >
                    <FormWidget
                        component={'Number'}
                        name="showAttributes"
                        label={<I18n token="production.form.field.noOfEpisodes" />}
                        mapTo="noOfEpisodes"
                    />
                </FormItem>
                <FormItem hidden={shouldHideShowAttribute(PRODUCTION_TYPES_OR_SUB_TYPES.EPISODE, state, hiddenFields)}>
                    <FormWidget
                        component={'Number'}
                        name="showAttributes"
                        label={<I18n token="production.form.field.episodeNo" />}
                        mapTo="episodeNo"
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.productionStatus]}>
                    <FormWidget
                        component="MetadataAutocomplete"
                        metadataName="ProductionStatus"
                        name="productionStatus"
                        label={<I18n token="production.form.field.production.status" />}
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.locations]}>
                    <FormWidget
                        component="Location"
                        name="locations"
                        label={<I18n token="production.form.field.locations" />}
                        multiple
                        limitTags={1}
                    />
                </FormItem>
                <OwnerOrganizationFormItem
                    hideOwnerOrganization={hiddenFields[Production_Field_Names.ownerOrganization]}
                />
            </FormContainer>
            {!hiddenFields[Production_Field_Names.scheduleSection] && (
                <FormContainer titleI18nToken={'production.form.schedule'}>
                    <ProductionScheduleFormWidget />
                    <FormItem half>
                        <FormWidget
                            component="Date"
                            name="releaseDate"
                            label={<I18n token="production.form.field.releaseDate" />}
                        />
                    </FormItem>
                    <FormItem half>
                        <FormWidget
                            name="productionYears"
                            label={<I18n token="production.form.field.productionYears" />}
                        />
                    </FormItem>
                </FormContainer>
            )}
            <ProductionTrackFormItems
                onCreateHandle={onCreateHandle}
                hidden={hiddenFields[Production_Field_Names.productionTrack]}
                focusField={focusField}
            />
            <FormContainer
                titleI18nToken={'production.form.about'}
                hideTitle={hiddenFields[Production_Field_Names.sectionTitle]}
            >
                <FormItem hidden={hiddenFields[Production_Field_Names['productionGroup.productionReference']]}>
                    <FormWidget
                        name="productionGroup"
                        component="ReferenceAutocomplete"
                        entity="Production"
                        label={<I18n token="production.form.field.associatedTitle" />}
                    />
                </FormItem>
                <FormItem hidden={hiddenFields[Production_Field_Names['summary.about']]}>
                    <FormWidget
                        name="about"
                        component={'TextArea'}
                        label={<I18n token="production.form.field.summary" />}
                        minRows={2}
                    />
                </FormItem>
                <FormItem hidden={hiddenFields[Production_Field_Names.showAttributes]}>
                    <FormWidget
                        component="Autocomplete"
                        name="showAttributes"
                        label="Runtime"
                        options={runtimesToSelectOptions(runtimes)}
                        mapTo="runtime"
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names['budget.currencyCode']]}>
                    <FormWidget
                        component="Currency"
                        name="budget.currencyCode"
                        label={<I18n token="production.form.field.currency" />}
                        disableClearable
                    />
                </FormItem>
                <FormItem half hidden={hiddenFields[Production_Field_Names.budget]}>
                    <FormWidget component="Money" name="budget" label={<I18n token="production.form.field.budget" />} />
                </FormItem>
            </FormContainer>
        </FormPanel>
    );
}
